import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ALLOTMENT_FULL,
  hasAutosaveContent,
  type ExistingFamilyConflict,
  type ReferralDto,
} from '@hv/shared';
import { ApiError, patch, post } from '../api/client';
import { keys } from '../api/hooks';
import { existingFamilyFrom, mergeSaved, toRequest, type DraftForm } from './draft';

export const AUTOSAVE_DELAY_MS = 1500;

export type SaveState =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'saved'; at: string }
  | { status: 'error'; error: unknown }
  | { status: 'conflict'; message: string }
  | { status: 'existing_family'; existing: ExistingFamilyConflict }
  | { status: 'gone' };

interface Options {
  referral: ReferralDto | null;
  initial: DraftForm;
  onCreated: (id: string) => void;
}

export function useDraftSaver({ referral, initial, onCreated }: Options) {
  const qc = useQueryClient();
  const formRef = useRef<DraftForm>(initial);
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;
  const [form, setFormState] = useState<DraftForm>(initial);
  const idRef = useRef<string | null>(referral?.id ?? null);
  const versionRef = useRef<number>(referral?.version ?? 0);
  const editSeq = useRef(0);
  const savedSeq = useRef(0);
  const blocked = useRef(false);
  const chain = useRef<Promise<boolean>>(Promise.resolve(true));
  const [saveState, setSaveState] = useState<SaveState>(
    referral ? { status: 'saved', at: referral.updatedAt } : { status: 'idle' },
  );

  const replace = useCallback((next: DraftForm) => {
    formRef.current = next;
    setFormState(next);
  }, []);

  const edit = useCallback(
    (fn: (f: DraftForm) => DraftForm) => {
      editSeq.current++;
      replace(fn(formRef.current));
    },
    [replace],
  );

  const doSave = useCallback(
    async (force: boolean): Promise<boolean> => {
      if (blocked.current) return false;
      if (!force && editSeq.current === savedSeq.current) return true;
      const seq = editSeq.current;
      const req = toRequest(formRef.current);
      const id = idRef.current;
      setSaveState({ status: 'saving' });
      try {
        const saved = id
          ? await patch<ReferralDto>(`/me/referrals/${id}`, {
              version: versionRef.current,
              cycleId: req.cycleId,
              currentStep: req.currentStep,
              countsTowardAllotment: req.countsTowardAllotment,
              payload: req.payload,
            })
          : await post<ReferralDto>('/me/referrals', {
              cycleId: req.cycleId,
              currentStep: req.currentStep,
              countsTowardAllotment: req.countsTowardAllotment,
              payload: req.payload,
            });
        idRef.current = saved.id;
        versionRef.current = saved.version;
        savedSeq.current = Math.max(savedSeq.current, seq);
        replace(mergeSaved(formRef.current, req.sentUids, saved));
        qc.setQueryData(keys.myReferral(saved.id), saved);
        void qc.invalidateQueries({ queryKey: keys.myReferrals, exact: true });
        void qc.invalidateQueries({ queryKey: keys.myAllotment });
        setSaveState({ status: 'saved', at: saved.updatedAt });
        if (!id) onCreatedRef.current(saved.id);
        return true;
      } catch (err) {
        console.error('Saving referral draft failed', { referralId: id, err });
        const existing = existingFamilyFrom(err);
        if (existing) {
          setSaveState({ status: 'existing_family', existing });
        } else if (err instanceof ApiError && err.status === 409 && err.code === 'STALE_REFERRAL') {
          blocked.current = true;
          setSaveState({ status: 'conflict', message: err.message });
        } else if (
          err instanceof ApiError &&
          (err.status === 404 || (err.status === 409 && err.code === 'REFERRAL_NOT_DRAFT'))
        ) {
          blocked.current = true;
          setSaveState({ status: 'gone' });
        } else if (err instanceof ApiError && err.status === 409 && err.code === ALLOTMENT_FULL) {
          editSeq.current++;
          replace({ ...formRef.current, countsTowardAllotment: false });
          void qc.invalidateQueries({ queryKey: keys.myAllotment });
          setSaveState({ status: 'error', error: err });
        } else {
          setSaveState({ status: 'error', error: err });
        }
        return false;
      }
    },
    [qc, replace],
  );

  const save = useCallback(
    (force = false) => {
      const next = chain.current.then(() => doSave(force));
      chain.current = next;
      return next;
    },
    [doSave],
  );

  const dirty = () => editSeq.current !== savedSeq.current;
  const worthSaving = () => idRef.current !== null || hasAutosaveContent(formRef.current.payload);

  useEffect(() => {
    if (!dirty() || !hasAutosaveContent(form.payload)) return;
    const timer = setTimeout(() => void save(), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [form, save]);

  useEffect(
    () => () => {
      if (dirty() && hasAutosaveContent(formRef.current.payload)) void save();
    },
    [save],
  );

  const reload = useCallback(
    (latest: ReferralDto, next: DraftForm) => {
      blocked.current = false;
      idRef.current = latest.id;
      versionRef.current = latest.version;
      savedSeq.current = editSeq.current;
      replace(next);
      setSaveState({ status: 'saved', at: latest.updatedAt });
    },
    [replace],
  );

  return {
    form,
    formRef,
    edit,
    replace,
    save,
    saveState,
    reload,
    worthSaving,
    referralId: () => idRef.current,
    version: () => versionRef.current,
    stop: () => {
      blocked.current = true;
    },
    flagExistingFamily: (existing: ExistingFamilyConflict) =>
      setSaveState({ status: 'existing_family', existing }),
  };
}
